import { BankAccountsUpdateRequest } from "../../features/api-interfaces";
import { getMessage } from "../../features/error/messages";
import {
  isHalfNumberLengthValid,
  isHalfNumberValid,
  isKatakanaFullCharPattern,
  isKatakanaValid,
  isLengthValid,
  isMailValid,
  isNull,
} from "../../utils/inputValidation";
import { COMMON_LABELS } from "../default-constants";
import { FIELDS_LENGTH, IErrMsg, LABELS } from "./constants";

/**
 * メールアドレスのバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validEmail = (value: string): string | undefined => {
  if (!isMailValid(value)) {
    return getMessage("KGRERR025").label;
  } else if (FIELDS_LENGTH.NOTIFICATION_EMAIL < value.length) {
    return getMessage(
      "KGRERR024",
      LABELS.NOTIFICATION_EMAIL,
      FIELDS_LENGTH.NOTIFICATION_EMAIL
    ).label;
  }
  return undefined;
};

/**
 * 必須項目のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validRequired = (value: string): string | undefined => {
  if (isNull(value)) {
    return getMessage("KGRERR055").label;
  }
  return undefined;
};

/**
 * 口座番号のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validBankAccountNumber = (value: string): string | undefined => {
  if (isNull(value)) {
    return getMessage("KGRERR055").label;
  } else if (!isHalfNumberValid(value)) {
    return getMessage("KGRERR006").label;
  } else if (!isLengthValid(value, FIELDS_LENGTH.BANK_ACCOUNT_NUMBER)) {
    return getMessage("KGRERR059", FIELDS_LENGTH.BANK_ACCOUNT_NUMBER).label;
  }
  return undefined;
};

/**
 * 口座名義カナのバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validBankAccountHolderKana = (
  value: string
): string | undefined => {
  if (isNull(value)) {
    return getMessage("KGRERR055").label;
  } else if (!isKatakanaFullCharPattern(value)) {
    return getMessage("KGRERR162").label;
  } else if (!isLengthValid(value, FIELDS_LENGTH.BANK_ACCOUNT_HOLDER_KANA)) {
    return getMessage("KGRERR058", FIELDS_LENGTH.BANK_ACCOUNT_HOLDER_KANA)
      .label;
  }
  return undefined;
};

/**
 * 法人番号のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validCorporateNumber = (value: string): string | undefined => {
  if (!isNull(value) && !isHalfNumberValid(value)) {
    return getMessage("KGRERR006").label;
  } else if (
    !isNull(value) &&
    !isHalfNumberLengthValid(value, FIELDS_LENGTH.CORPORATE_NUMBER)
  ) {
    return getMessage(
      "KGRERR065",
      COMMON_LABELS.CHAR_TYPE.HALF_NUMBER,
      FIELDS_LENGTH.CORPORATE_NUMBER
    ).label;
  }
  return undefined;
};

/**
 * 入金先事業者事業者名のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validCompanyName = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (!isNull(value) && !isLengthValid(value, FIELDS_LENGTH.COMPANY_NAME)) {
    return getMessage("KGRERR058", FIELDS_LENGTH.COMPANY_NAME).label;
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者事業者名カナのバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validCompanyNameKana = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (
    !isNull(value) &&
    !isLengthValid(value, FIELDS_LENGTH.COMPANY_NAME_KANA)
  ) {
    return getMessage("KGRERR058", FIELDS_LENGTH.COMPANY_NAME_KANA).label;
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者郵便番号のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validPostalCode = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (!isNull(value) && !isHalfNumberValid(value)) {
    return getMessage("KGRERR006").label;
  } else if (
    !isNull(value) &&
    !isLengthValid(value, FIELDS_LENGTH.POSTAL_CODE)
  ) {
    return getMessage("KGRERR073", FIELDS_LENGTH.POSTAL_CODE).label;
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者本社住所（その他）のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validPaymentTargetCompanyAddress2 = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (!isNull(value) && !isLengthValid(value, FIELDS_LENGTH.ADDRESS_OTHERS)) {
    return getMessage("KGRERR058", FIELDS_LENGTH.ADDRESS_OTHERS).label;
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者本社住所カナのバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validPaymentTargetCompanyAddress1Kana = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (!isNull(value)) {
    if (!isKatakanaValid(value)) {
      return getMessage("KGRERR026").label;
    } else if (!isLengthValid(value, FIELDS_LENGTH.ADDRESS_KANA)) {
      return getMessage("KGRERR059", FIELDS_LENGTH.ADDRESS_KANA).label;
    }
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者電話番号のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @returns {string | undefined} エラーメッセージ
 */
export const validPhoneNumber = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  if (!isNull(value) && !isHalfNumberValid(value)) {
    return getMessage("KGRERR006").label;
  } else if (
    !isNull(value) &&
    !isLengthValid(value, FIELDS_LENGTH.PHONE_NUMBER)
  ) {
    return getMessage("KGRERR058", FIELDS_LENGTH.PHONE_NUMBER).label;
  }
  return validPaymentTargetCompany(value, request);
};

/**
 * 入金先事業者情報のいずれかの入力がされているか判定し、結果を返却する.
 * @param {BankAccountsUpdateRequest} request 入金口座情報更新リクエスト
 * @returns {boolean} true: 入力あり
 */
export const hasCorpInfo = (request: BankAccountsUpdateRequest): boolean => {
  return [
    request.companyName,
    request.companyNameKana,
    request.postalCode,
    request.paymentTargetCompanyAddress1,
    request.paymentTargetCompanyAddress2,
    request.paymentTargetCompanyAddress1Kana,
    request.phoneNumber,
  ].some((field) => field.trim() !== "");
};

/**
 * 入金先事業者情報のバリデーションチェックを行う.
 * @param {string} value チェック文字
 * @param {BankAccountsUpdateRequest} request 入金口座情報更新リクエスト
 * @returns {string | undefined} エラーメッセージ
 */
export const validPaymentTargetCompany = (
  value: string,
  request: BankAccountsUpdateRequest
): string | undefined => {
  const hasCorpNo = !isNull(request.corporateNumber);
  if (hasCorpNo && hasCorpInfo(request) && isNull(value)) {
    return getMessage("KGRERR055").label;
  }
  return undefined;
};

/**
 * 入金口座情報更新リクエストすべてのバリデーションチェックを行う.
 * @param {BankAccountsUpdateRequest} request 入金口座情報更新リクエスト
 * @returns {IErrMsg} エラーメッセージリスト
 */
export const validRequest = (request: BankAccountsUpdateRequest): IErrMsg => {
  return {
    notificationEmail: validEmail(request.notificationEmail),
    fnnInsMthStrCd: validRequired(request.fnnInsMthStrCd),
    fnnInsBrnCd: validRequired(request.fnnInsBrnCd),
    bankAccountNumber: validBankAccountNumber(request.bankAccountNumber),
    bankAccountHolderKana: validBankAccountHolderKana(
      request.bankAccountHolderKana
    ),
    corporateNumber: validCorporateNumber(request.corporateNumber),
    companyName: validCompanyName(request.companyName, request),
    companyNameKana: validCompanyNameKana(request.companyNameKana, request),
    postalCode: validPostalCode(request.postalCode, request),
    paymentTargetCompanyAddress1: validPaymentTargetCompany(
      request.paymentTargetCompanyAddress1,
      request
    ),
    paymentTargetCompanyAddress2: validPaymentTargetCompanyAddress2(
      request.paymentTargetCompanyAddress2,
      request
    ),
    paymentTargetCompanyAddress1Kana: validPaymentTargetCompanyAddress1Kana(
      request.paymentTargetCompanyAddress1Kana,
      request
    ),
    phoneNumber: validPhoneNumber(request.phoneNumber, request),
  };
};
